import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowUpRight, BadgeCheck, Layers, LockKeyhole, Shield, TrendingUp } from "lucide-react";
import PricingSection from "../components/PricingSection";
import PublicNav from "../components/PublicNav";
import { PRODUCT_BOUNDARY, PRODUCT_IDENTITY, PRODUCT_PROBLEM, PRODUCT_SUBTITLE } from "../lib/story";
import { captureUtmFromUrl, trackVisitorEvent, withMarketingQuery } from "../lib/visitorAnalytics";

const PROCESS = [
  { id: "why", label: "WHY", note: "Business problem first", icon: Activity },
  { id: "decide", label: "DECIDE", note: "Evidence-backed hypotheses", icon: TrendingUp },
  { id: "deliver", label: "DELIVER", note: "G1–G8 governance path", icon: Shield },
  { id: "prove", label: "PROVE", note: "Expected vs actual", icon: BadgeCheck },
];

const STATS = [
  { value: "1,200+", label: "Applications assessed", icon: Layers },
  { value: "85%", label: "Faster decisions", icon: TrendingUp },
  { value: "40%", label: "Risk reduction", icon: Shield },
  { value: "100%", label: "Evidence-backed", icon: BadgeCheck },
];

export default function Landing() {
  useEffect(() => {
    captureUtmFromUrl();
    trackVisitorEvent("page_view", { page: "landing" });
  }, []);

  const onDemo = () => trackVisitorEvent("demo_clicked", { source: "landing_hero" });
  const onTrial = () => {
    trackVisitorEvent("trial_started", { source: "landing_hero" });
    trackVisitorEvent("signup_started", { source: "landing_hero" });
  };
  const onPricing = () => trackVisitorEvent("pricing_viewed", { source: "landing_hero" });

  return (
    <main className="login-screen landing-screen landing-full" data-testid="landing-page">
      <div className="login-grid" />
      <PublicNav />
      <section className="login-hero landing-hero-wide">
        <div className="eyebrow"><span className="signal" /> NEXTPROMIND · YUG360-MIND</div>
        <h1>From “Why Modernize?”<br />to <em>Value Proven.</em></h1>
        <p className="login-identity" data-testid="landing-identity">
          <strong>{PRODUCT_IDENTITY}.</strong> {PRODUCT_SUBTITLE}
        </p>
        <p>{PRODUCT_BOUNDARY} Simulation is the destination, not this sample workspace. Execution stays in Jira, Azure DevOps, ServiceNow, CI/CD and the cloud.</p>
        <p className="login-problem" data-testid="landing-problem">{PRODUCT_PROBLEM}</p>

        <div className="landing-hero-ctas" data-testid="landing-hero-ctas">
          <Link data-testid="landing-explore-demo" className="primary-button" to="/login?demo=1" onClick={onDemo}>
            Explore Live Demo <ArrowUpRight size={16} />
          </Link>
          <Link
            data-testid="landing-start-trial"
            className="secondary-button"
            to={withMarketingQuery("/signup?plan=trial")}
            onClick={onTrial}
          >
            Start Free Trial
          </Link>
          <Link data-testid="landing-view-pricing" className="text-button" to="/pricing" onClick={onPricing}>
            View Pricing →
          </Link>
        </div>

        <div className="landing-process" id="how-it-works" data-testid="landing-process">
          {PROCESS.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.id} style={{ display: "contents" }}>
                <div className="landing-process-step">
                  <div className="landing-process-icon"><Icon size={20} /></div>
                  <strong>{step.label}</strong>
                  <span>{step.note}</span>
                </div>
                {index < PROCESS.length - 1 && <div className="landing-process-line" aria-hidden="true" />}
              </div>
            );
          })}
        </div>

        <div className="landing-stats" data-testid="landing-stats">
          {STATS.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="landing-stat">
                <div className="landing-stat-head"><Icon size={16} /></div>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <aside className="login-panel landing-side-panel" data-testid="landing-cta">
        <div className="brand-mark">YUG<span>360</span><small>— MIND</small></div>
        <p className="kicker">{PRODUCT_IDENTITY.toUpperCase()}</p>
        <h2>Northstar sample workspace</h2>
        <p className="muted">Explore the guided demo without registration, or start a 14-day trial on the live workspace.</p>
        <div className="login-actions">
          <Link data-testid="landing-open-demo" className="primary-button full" to="/login?demo=1" onClick={onDemo}>
            Explore Live Demo <ArrowUpRight size={16} />
          </Link>
          <Link
            data-testid="landing-go-register"
            className="secondary-button full"
            to={withMarketingQuery("/signup?plan=trial")}
            onClick={onTrial}
          >
            Start Free Trial
          </Link>
          <Link data-testid="landing-go-sso" className="text-button" to="/login/sso">
            <LockKeyhole size={16} /> Continue with SSO
          </Link>
          <Link data-testid="landing-go-login" className="text-button" to="/login">
            Sign in with email →
          </Link>
        </div>
        <p className="muted small-note">Demo: {`admin@yug360.demo`} · Trial joins Northstar (NRG-01) portfolio</p>
        <div className="login-footer"><LockKeyhole size={14} /> Tenant-isolated · Human-authorized · Model=None ready</div>
      </aside>

      <div className="landing-pricing-wrap">
        <PricingSection onViewed={() => trackVisitorEvent("pricing_viewed", { source: "landing_section" })} />
      </div>

      <footer className="landing-footer landing-footer-wide">
        <Link to="/pricing">Pricing</Link>
        <Link to="/contact-sales">Contact Sales</Link>
        <Link to="/login">Sign in</Link>
        <small>© 2026 NextProMind · YUG360-MIND</small>
      </footer>
    </main>
  );
}
